import {useEffect, useState} from 'react';
import './EasyEstimatorGrid.scss'
import {
    Column,
    Format,
    Item as GridToolbarItem,
    Lookup, Paging, RemoteOperations
} from 'devextreme-react/data-grid';
import dayjs from 'dayjs';
import {useGetEstimates} from "../api/get-estimates";
import {Link, useLocation} from 'react-router-dom';
import {LoadPagePanel, SyncDataGrid} from '../../../../components';
import {OLD_PORTALS_URL} from '../../../../config/constants';
import {ProductLine, QuoteTypes} from '../../../../types/enums';
import CheckBox from 'devextreme-react/check-box';
import SelectBox from 'devextreme-react/select-box';
import {Item, Toolbar} from 'devextreme-react/toolbar';
import {useGetCompany} from '../../../../api/company';
import {useAuth} from '../../../../contexts/auth';
import CreateEstimateButton from "./CreateEstimateButton";
import {useGetEstimatesFilterLookupData} from "../api/get-estimates-filter-lookup-data";

export default function EasyEstimatorGrid() {

    const datePeriods = [
        {name: "Last 90 Days", dateFrom: dayjs().subtract(90, 'day').toDate().toDateString()},
        {name: "Last 6 Months", dateFrom: dayjs().subtract(6, 'month').toDate().toDateString()},
        {name: "Last Year", dateFrom: dayjs().subtract(1, 'year').toDate().toDateString()},
        {name: "Last 2 Years", dateFrom: dayjs().subtract(2, 'year').toDate().toDateString()},
        {name: "Last 3 Years", dateFrom: dayjs().subtract(3, 'year').toDate().toDateString()},
        {name: "Entire History", dateFrom: null}
    ];

    const [dateFrom, setDateFrom] = useState<string | null>(datePeriods[0].dateFrom);
    const [closedOrders, setClosedOrders] = useState(false);

    const estimates = useGetEstimates(closedOrders, dateFrom ? new Date(dateFrom) : null);
    const {data, isLoading} = useGetEstimatesFilterLookupData();

    const {user} = useAuth();
    const company = useGetCompany(user?.companyId);

    const location = useLocation();
    const returnUrl = encodeURIComponent(location.pathname + location.search);

    return (
        <>
            <Toolbar className='easy-estimator-filter'>
                <Item location='before'>
                    <SelectBox
                        className='estimate-date-period-selectbox'
                        value={dateFrom}
                        valueExpr="dateFrom"
                        displayExpr="name"
                        dataSource={datePeriods}
                        onValueChanged={(e) => setDateFrom(e.value)}
                    />
                </Item>
                <Item location='before'>
                    <CheckBox
                        className='closed-estimates-checkbox'
                        value={closedOrders}
                        text={"Closed/Expired Estimates"}
                        onValueChanged={(e) => {
                            setClosedOrders(e.value);
                        }}/>
                </Item>
            </Toolbar>

            <SyncDataGrid
                stateStorageKey='dealer-easy-estimator-grid-v2'
                className={'dx-card wide-card'}
                dataSource={data ? estimates : []}
                syncLookupFilterValues={false}
                height="62vh"
                additionalToolbarItems={[
                    <GridToolbarItem
                        location={'before'}
                        key='new-estimate'
                    >
                        <CreateEstimateButton company={company?.data} userToken={data?.userToken}/>
                    </GridToolbarItem>
                ]}
            >
                <RemoteOperations
                    filtering={true}
                    sorting={true}
                    paging={true}
                />
                <Paging pageSize={1000}/>
                <Column
                    dataField={'quoteNumber'}
                    caption={company.data?.SFI ? 'Lead/Estimate #' : 'Estimate #'}
                    dataType="string"
                    cellRender={(row: any) => {
                        return <Link to={`/dealer/estimate/${row.data.quoteID}?returnUrl=${returnUrl}`}>
                            {row.data.quoteNumber}
                        </Link>;
                    }}
                />
                <Column
                    caption={'EE Tool'}
                    dataType="string"
                    cssClass='ee-tool'
                    alignment='center'
                    cellRender={(cell: any) => {
                        if (cell.data.imeStatus !== 'SALE' && cell.data.quoteTypeID !== QuoteTypes.WinApp)
                            return <>
                                <Link to={`/dealer/easy-estimator/${cell.data.quoteID}`} title="EE Pro">
                                    <i className='dx-icon dx-icon-Inscyth-EEPro'/>
                                </Link>
                                {
                                    !!company.data?.easyEstimatorUrl &&
                                    <a href={company.data?.easyEstimatorUrl + '?userToken=' + data?.userToken + '&loadEstimate=' + cell.data.guid}
                                       target="_blank" rel="noreferrer" title="Estimator">
                                        <i className='dx-icon dx-icon-Inscyth-Easy-Estimator'/>
                                    </a>
                                }
                            </>;
                        else if (cell.data.quoteTypeID === QuoteTypes.WinApp)
                            return <>
                                <Link onClick={() => {
                                    alert("Estimate was edited by Sage Support team and can no longer be accessed through the estimator tools");
                                }}
                                      rel="noreferrer" title="Estimator" role="button" to="">
                                    <i className='dx-icon dx-icon-Inscyth-Info'/>
                                </Link>
                            </>;
                        else
                            return <></>;
                    }}
                />
                <Column
                    dataField={'customerName'}
                    caption={'Customer'}
                    dataType="string"
                />
                <Column
                    dataField={'projectNumber'}
                    caption={'Project Number'}
                    dataType="string"
                />
                <Column
                    dataField={'colorGroup'}
                    caption={'Color Group'}
                    dataType="string"
                />
                <Column
                    dataField={'colorName'}
                    caption={'Color Name'}
                    dataType="string"
                />
                <Column
                    dataField={'retailAmount'}
                    caption={'Retail Amount'}
                    dataType="number"
                    editorOptions={{format: '$#,##0.##', showClearButton: true}}
                >
                    <Format type="currency" precision={2}></Format>
                </Column>
                <Column
                    dataField={'quoteDate'}
                    caption={'Estimate Date'}
                    dataType="date"
                />
                <Column
                    dataField={'createdOn'}
                    caption={'Creation Date'}
                    dataType="datetime"
                />
                <Column
                    dataField={'expirationDate'}
                    caption={'Expires On'}
                    dataType="date"
                />
                <Column
                    dataField={'status'}
                    caption={'Status'}
                    dataType="string"
                >
                    <Lookup
                        dataSource={data?.statuses}
                    />
                </Column>
                {
                    company.data?.IME &&
                    <Column
                        dataField={'imeStatus'}
                        caption={'IME Status'}
                        dataType="string"
                    >
                        <Lookup
                            dataSource={data?.imeStatuses}
                        />
                    </Column>
                }
                <Column
                    dataField={'dealerNumber'}
                    caption={'Store #'}
                    dataType="string"
                />
                <Column
                    dataField={'dealerName'}
                    caption={'Store Name'}
                    dataType="string"
                />
                <Column
                    dataField={'actionRequired'}
                    caption={'Action Required'}
                    dataType="string"
                >
                    <Lookup
                        dataSource={data?.actionsRequired}
                    />
                </Column>
                <Column
                    dataField={'roNumber'}
                    caption={'RO #'}
                    dataType="string"
                    cellRender={(cell: any) => {
                        return (cell.data.matchedROID ?
                                <Link to={`/dealer/retail-order/${cell.data.matchedROID}`}>
                                    {cell.data.roNumber}
                                </Link>
                                :
                                <></>
                        );
                    }}
                />
                <Column
                    dataField={'createdByName'}
                    caption={'Created By'}
                    dataType="string"
                />
            </SyncDataGrid>

            <LoadPagePanel visible={isLoading}/>
        </>
    )
}